import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { submitDebugStory } from '../api/client';
import type { DebugStoryResult, StoryType } from '../api/types';
import { useConfigStore } from '../stores/configStore';
import ConfigPanel from '../components/config/ConfigPanel';
import ScoreBreakdown from '../components/scores/ScoreBreakdown';
import DecisionFlow from '../components/pipeline/DecisionFlow';
import PromptInspector from '../components/prompts/PromptInspector';

export default function ConfigPlayground() {
  const { config, isModified, resetToDefaults } = useConfigStore();
  const [storyText, setStoryText] = useState('');
  const [city, setCity] = useState('Yerevan');
  const [storyType, setStoryType] = useState<StoryType>('stranger');
  const [persistStory, setPersistStory] = useState(true);
  const [result, setResult] = useState<DebugStoryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async () => {
    if (!storyText.trim() || storyText.length < 50) {
      setError('Story must be at least 50 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null); // Clear previous results

    // Scroll to loading indicator
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    try {
      // Don't persist if config is modified (custom config shouldn't affect real DB)
      const shouldSkipPersist = isModified || !persistStory;
      const debugResult = await submitDebugStory(
        storyText,
        city,
        storyType,
        isModified ? config : undefined,
        shouldSkipPersist
      );
      setResult(debugResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Config Playground</h1>
        {isModified && (
          <button onClick={resetToDefaults} className="btn btn-secondary text-sm">
            Reset to Defaults
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Config Panel */}
        <div className="lg:col-span-1">
          <ConfigPanel />
        </div>

        {/* Right: Test Story */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Story</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    placeholder="Yerevan"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Story Type</label>
                  <select
                    value={storyType}
                    onChange={(e) => setStoryType(e.target.value as StoryType)}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="stranger">Stranger</option>
                    <option value="crush">Crush</option>
                    <option value="ex">Ex</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="persistStory"
                  checked={persistStory}
                  onChange={(e) => setPersistStory(e.target.checked)}
                  disabled={isModified}
                  className="w-4 h-4 text-pebbl-600 rounded"
                />
                <label htmlFor="persistStory" className={`text-sm ${isModified ? 'text-gray-400' : 'text-gray-600'}`}>
                  Save story to database
                </label>
                {isModified && (
                  <span className="text-xs text-amber-600">(disabled when config modified)</span>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Story Text (min 50 chars)
                </label>
                <textarea
                  value={storyText}
                  onChange={(e) => setStoryText(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm h-32 resize-y"
                  placeholder="Last Friday evening at Mirzoyan Library, I was sitting at the third terrace when a gust of wind blew my papers everywhere. A tall guy with glasses and a green jacket helped me collect them. We made eye contact and smiled but I was too shy to say anything..."
                />
                <div className="text-xs text-gray-400 mt-1">
                  {storyText.length} characters
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 rounded p-2">
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading || storyText.length < 50}
                className="btn btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Submit Test Story'}
              </button>

              {isModified ? (
                <div className="text-xs text-amber-600 bg-amber-50 rounded p-2">
                  Using modified config. Story will NOT be persisted.
                </div>
              ) : persistStory ? (
                <div className="text-xs text-green-600 bg-green-50 rounded p-2">
                  Story will be saved to database and available for matching.
                </div>
              ) : (
                <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                  Test mode - story will NOT be persisted.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div ref={resultsRef} className="border-t pt-6">
          <div className="card text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-pebbl-200 border-t-pebbl-600 mb-4"></div>
            <div className="text-gray-600 font-medium">Processing story...</div>
            <div className="text-sm text-gray-500 mt-1">Parsing with Claude, searching vectors, running gates...</div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div ref={resultsRef} className="space-y-6 border-t pt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Results</h2>
            <div className="text-sm text-gray-500">
              Total time: {result.totalLatencyMs}ms
            </div>
          </div>

          {/* Pipeline Flow */}
          <DecisionFlow traces={result.pipelineTrace} />

          {/* Matches Found */}
          {result.matches.length > 0 ? (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">
                Top Matches ({result.matches.length})
              </h3>
              <div className="space-y-4">
                {result.matches.map((match, i) => (
                  <div key={match.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm text-gray-500">#{i + 1}</span>
                      <div className="text-right">
                        <div className="text-lg font-bold text-pebbl-600">
                          {((match.scores.final ?? match.scores.overall) * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          {match.scores.llmJudge != null && (
                            <span>LLM: {(match.scores.llmJudge * 100).toFixed(0)}%</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {match.storyB.snippet}
                    </p>
                    <ScoreBreakdown scores={match.scores} compact />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card text-center text-gray-500 py-8">
              No matches found with current config
            </div>
          )}

          {/* Candidate Debug */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">
              All Candidates ({result.candidates.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Story ID</th>
                    <th className="text-left p-2">Overall</th>
                    <th className="text-left p-2">Event</th>
                    <th className="text-left p-2">Location</th>
                    <th className="text-left p-2">Bypass</th>
                    <th className="text-left p-2">Gates</th>
                    <th className="text-left p-2">Filtered At</th>
                  </tr>
                </thead>
                <tbody>
                  {result.candidates.map((c) => (
                    <tr key={c.storyId} className="border-t hover:bg-gray-50">
                      <td className="p-2">
                        <Link
                          to={`/stories/${c.storyId}`}
                          target="_blank"
                          className="font-mono text-xs text-pebbl-600 hover:underline"
                        >
                          {c.storyId}
                        </Link>
                      </td>
                      <td className="p-2">{(c.detailedScores.overall * 100).toFixed(0)}%</td>
                      <td className="p-2">
                        <span className={c.gateResults.eventPass ? 'text-green-600' : 'text-red-600'}>
                          {(c.detailedScores.event * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="p-2">
                        <span className={c.gateResults.locationPass ? 'text-green-600' : 'text-red-600'}>
                          {(c.detailedScores.location * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="p-2 text-xs">
                        {c.gateResults.proximityBypass ? (
                          <span
                            className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"
                            title={`distance=${c.gateResults.proximityDistanceMeters ?? '—'}m, overlap=${c.gateResults.timeRangeOverlapSeconds ?? '—'}s`}
                          >
                            proximity ✓
                          </span>
                        ) : c.gateResults.proximityDistanceMeters != null ||
                          c.gateResults.timeRangeOverlapSeconds != null ? (
                          <span className="text-gray-400">
                            {c.gateResults.proximityDistanceMeters != null
                              ? `${Math.round(c.gateResults.proximityDistanceMeters)}m`
                              : '—'}
                            /
                            {c.gateResults.timeRangeOverlapSeconds != null
                              ? `${c.gateResults.timeRangeOverlapSeconds}s`
                              : '—'}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-2">
                        {c.gateResults.coverageCount}/4
                      </td>
                      <td className="p-2">
                        {c.filteredAt ? (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                            {c.filteredAt}
                          </span>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            passed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Prompts */}
          <PromptInspector prompts={result.prompts} />
        </div>
      )}
    </div>
  );
}
